import {
  signInAnonymously,
  signOut,
  type Auth,
  type User,
} from "firebase/auth";
import { auth } from "../config/firebase";

export interface GuestSharedAccessActor {
  uid: string;
  isAnonymous: boolean;
}

export interface GuestSharedAccessAuthAdapter<
  Actor extends GuestSharedAccessActor,
> {
  currentUser: () => Actor | null;
  isOnline?: () => boolean;
  refresh: (actor: Actor) => Promise<unknown>;
  signOut: () => Promise<void>;
  signInAnonymously: () => Promise<Actor>;
}

const recoverableAnonymousActorErrors = new Set([
  "auth/invalid-user-token",
  "auth/user-disabled",
  "auth/user-not-found",
  "auth/user-token-expired",
]);

const isRecoverableAnonymousActorError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "code" in error &&
  typeof error.code === "string" &&
  recoverableAnonymousActorErrors.has(error.code);

export const createGuestSharedAccessActor = <
  Actor extends GuestSharedAccessActor,
>(
  adapter: GuestSharedAccessAuthAdapter<Actor>,
): (() => Promise<Actor>) => {
  let acquisition: Promise<Actor> | null = null;

  return async () => {
    if (!acquisition) {
      acquisition = (async () => {
        const currentActor = adapter.currentUser();
        if (currentActor && !currentActor.isAnonymous) {
          return currentActor;
        }

        if (currentActor) {
          if (adapter.isOnline?.() === false) {
            return currentActor;
          }
          try {
            await adapter.refresh(currentActor);
            return currentActor;
          } catch (error) {
            if (!isRecoverableAnonymousActorError(error)) {
              throw error;
            }
            await adapter.signOut();
          }
        }

        return adapter.signInAnonymously();
      })().finally(() => {
        acquisition = null;
      });
    }

    return acquisition;
  };
};

const firebaseAuthAdapter = (
  firebaseAuth: Auth,
): GuestSharedAccessAuthAdapter<User> => ({
  currentUser: () => firebaseAuth.currentUser,
  isOnline: () => typeof navigator === "undefined" || navigator.onLine,
  refresh: (actor) => actor.getIdToken(true),
  signOut: () => signOut(firebaseAuth),
  signInAnonymously: async () => (await signInAnonymously(firebaseAuth)).user,
});

export const requireGuestSharedAccessActor = createGuestSharedAccessActor(
  firebaseAuthAdapter(auth),
);
